/*
  Rating graph beside the reviews (product page).

  Lipscore's review list does not draw a breakdown of the scores, and its API
  has no summary call for one, so the bars are counted here from the review
  data that same API returns. Ratings left without any writing are included in
  the headline average and total, but Lipscore does not say which star each of
  them gave - so the bars only cover written reviews, and the line under them
  says so.

  The list itself is moved into a right-hand column with the graph on the left.
  Below 990px the two stack, graph first.

  Loading early. Lipscore's own script arrives after the page, waits, then
  fetches and draws - so the reviews used to land a good second and a half
  after everything else. This file is loaded async from the product section,
  starts the same request the moment it runs, and as soon as the review list's
  place exists it draws the graph and a first page of reviews straight away,
  in Lipscore's own markup so the cards look the same. When Lipscore finishes
  drawing its real list, with voting and paging, our copy is taken away. If
  Lipscore never draws at all, our copy simply stays.
*/
(function () {
  var LIST_SELECTOR = '#lipscore-review-list';
  var API = 'https://wapi.lipscore.com';
  var PER_PAGE = 100;
  var MAX_PAGES = 5;
  /* Lipscore shows five reviews to a page, so the early copy matches it. */
  var PREVIEW_COUNT = 5;

  /* Written into the page by main-product.liquid: Lipscore's public widget
     key, and the product as Lipscore knows it. */
  function source() {
    var node = document.getElementById('magnum-review-source');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* Lipscore scores out of ten, so a five star review arrives as 10. */
  function toStars(score) {
    var stars = Math.round(Number(score) / 2);
    if (stars < 1) return 1;
    if (stars > 5) return 5;
    return stars;
  }

  function starsMarkup(fraction) {
    var star =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>';
    // Five stars: joining an array of five puts four between them, plus one.
    var row = new Array(5).join(star) + star;
    return (
      '<span class="magnum-review-graph__stars">' +
      '<span class="magnum-review-graph__stars-empty">' + row + '</span>' +
      '<span class="magnum-review-graph__stars-full" style="width:' + fraction + '%">' + row + '</span>' +
      '</span>'
    );
  }

  function fetchJSON(url) {
    return fetch(url).then(function (response) {
      if (!response.ok) throw new Error('Lipscore returned ' + response.status);
      return response.json();
    });
  }

  /* The first call carries the totals as well as the first page of reviews. */
  function loadReviews(ids, apiKey) {
    var query =
      '?api_key=' + encodeURIComponent(apiKey) +
      '&sku=' + encodeURIComponent(ids.sku || '') +
      '&internal_id=' + encodeURIComponent(ids.productId || '') +
      /* The rating widget has to be asked for as well as the list, or the
         average and the review count come back empty. */
      '&widgets=r,rw_l&translate_to_lang=en&reviews_per_page=' + PER_PAGE;

    /* The product section may already have started this exact request. */
    var first = window.magnumReviewRequest || fetchJSON(API + '/initial_data/products/show' + query);

    return first.then(function (data) {
      var reviews = data.reviews || [];
      var wanted = Math.min(data.review_count || reviews.length, PER_PAGE * MAX_PAGES);
      var pages = [];

      for (var page = 2; reviews.length + (page - 2) * PER_PAGE < wanted && page <= MAX_PAGES; page++) {
        pages.push(
          fetchJSON(
            API + '/products/' + data.id + '/reviews?api_key=' + encodeURIComponent(apiKey) +
            '&page=' + page + '&reviews_per_page=' + PER_PAGE
          )
        );
      }

      if (!pages.length) return { data: data, reviews: reviews };

      return Promise.all(pages).then(function (rest) {
        rest.forEach(function (batch) {
          if (Array.isArray(batch)) reviews = reviews.concat(batch);
        });
        return { data: data, reviews: reviews };
      });
    });
  }

  function buildGraph(data, reviews) {
    var counts = [0, 0, 0, 0, 0];
    reviews.forEach(function (review) {
      counts[toStars(review.lipscore) - 1] += 1;
    });

    var written = reviews.length;
    var average = Number(data.lipscore) / 2;
    var votes = data.votes || written;

    /* Falls back to the average of the reviews in hand if Lipscore sends none. */
    if (!average) {
      var total = 0;
      reviews.forEach(function (review) {
        total += Number(review.lipscore) / 2;
      });
      average = written ? total / written : 0;
    }

    var graph = el('section', 'magnum-review-graph');
    graph.setAttribute('aria-label', 'Rating breakdown');

    var head = el('div', 'magnum-review-graph__head');
    head.appendChild(el('span', 'magnum-review-graph__average', average.toFixed(1)));
    head.insertAdjacentHTML('beforeend', starsMarkup((average / 5) * 100));
    head.appendChild(
      el('span', 'magnum-review-graph__votes', votes + (votes === 1 ? ' rating' : ' ratings'))
    );
    graph.appendChild(head);

    var bars = el('ul', 'magnum-review-graph__bars');
    for (var stars = 5; stars >= 1; stars--) {
      var count = counts[stars - 1];
      var row = el('li', 'magnum-review-graph__row');
      row.appendChild(el('span', 'magnum-review-graph__label', stars + (stars === 1 ? ' star' : ' stars')));

      var track = el('span', 'magnum-review-graph__track');
      var fill = el('span', 'magnum-review-graph__fill');
      fill.style.width = written ? (count / written) * 100 + '%' : '0%';
      track.appendChild(fill);
      row.appendChild(track);

      row.appendChild(el('span', 'magnum-review-graph__count', String(count)));
      bars.appendChild(row);
    }
    graph.appendChild(bars);

    graph.appendChild(
      el(
        'p',
        'magnum-review-graph__note',
        written === votes
          ? 'From all ' + written + ' reviews.'
          : 'From the ' + written + ' reviews with something written. The rest are ratings only.'
      )
    );

    return graph;
  }

  /* The "Reviews" heading is added by the script in main-product.liquid, which
     puts it directly before the list - now inside the right column. It belongs
     above both columns, so it is lifted back out whenever it turns up. */
  function keepHeadingAbove(layout) {
    var heading = document.getElementById('lipscore-heading');
    if (heading && layout.contains(heading)) {
      layout.parentNode.insertBefore(heading, layout);
    }
  }

  /* ---------- early copy of the first page of reviews ---------- */

  function decodeText(html) {
    // Review text arrives with HTML entities in it. Parse it as a document and
    // keep only the words, so nothing in a review can ever run as markup.
    var doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
  }

  function formatDate(iso) {
    var d = new Date(iso);
    if (isNaN(d)) return '';
    var dd = String(d.getDate()).padStart(2, '0');
    var mm = String(d.getMonth() + 1).padStart(2, '0');
    return dd + '.' + mm + '.' + d.getFullYear();
  }

  function previewCard(review) {
    var box = el('div', 'lipscore-review-box');
    var header = el('div', 'lipscore-review-header');
    var info = el('div', 'lipscore-review-info');
    var wrapper = el('div', 'lipscore-review-info-wrapper');

    var creation = el('div', 'lipscore-review-creation-info');
    var name = (review.user && (review.user.name || review.user.short_name)) || '';
    creation.appendChild(el('span', 'lipscore-strong lipscore-review-author', name));
    creation.appendChild(el('span', 'lipscore-review-created-at lipscore-review-date', formatDate(review.created_at)));
    wrapper.appendChild(creation);

    var stars = el('div', 'lipscore-review-rating-stars-wrapper');
    stars.insertAdjacentHTML('beforeend', starsMarkup((toStars(review.lipscore) / 5) * 100));
    wrapper.appendChild(stars);

    if (review.purchase_date) {
      var purchase = el('div', 'lipscore-review-purchase');
      var icon = el('div', 'lipscore-review-purchase-icon');
      icon.appendChild(el('div', 'lipscore-review-purchase-icon-badge'));
      icon.appendChild(el('span', null, ' buyer'));
      purchase.appendChild(icon);
      wrapper.appendChild(purchase);
    }

    info.appendChild(wrapper);
    header.appendChild(info);
    box.appendChild(header);

    var content = el('div', 'lipscore-review-content');
    var contentWrapper = el('div', 'lipscore-review-content-wrapper');
    contentWrapper.appendChild(el('div', 'lipscore-review-text', decodeText(review.text)));
    content.appendChild(contentWrapper);
    box.appendChild(content);

    return box;
  }

  function buildPreview(reviews) {
    var preview = el('div', 'magnum-reviews-preview');
    var container = el('div', 'lipscore-review-list-box-container');
    reviews.slice(0, PREVIEW_COUNT).forEach(function (review) {
      container.appendChild(previewCard(review));
    });
    preview.appendChild(container);
    return preview;
  }

  /* Lipscore has drawn its own list once a real review card is inside it. */
  function lipscoreHasDrawn(list) {
    return !!list.querySelector('.lipscore-review-box');
  }

  function place(list, graph, preview) {
    var layout = el('div', 'magnum-reviews-layout');
    var column = el('div', 'magnum-reviews-layout__list');

    list.parentNode.insertBefore(layout, list);
    layout.appendChild(graph);
    if (preview) column.appendChild(preview);
    column.appendChild(list);
    layout.appendChild(column);

    keepHeadingAbove(layout);
    new MutationObserver(function () {
      keepHeadingAbove(layout);
    }).observe(layout, { childList: true, subtree: true });

    if (preview) {
      var swap = function () {
        if (lipscoreHasDrawn(list) && preview.parentNode) {
          preview.parentNode.removeChild(preview);
          watcher.disconnect();
        }
      };
      var watcher = new MutationObserver(swap);
      watcher.observe(list, { childList: true, subtree: true });
      swap();
    }
  }

  /* The heading script in main-product.liquid names the section "Reviews"
     only once it can tell there are reviews. Lipscore tells it late, so the
     count is put on the list as soon as we have it. */
  function markCount(list, count) {
    list.setAttribute('data-magnum-review-count', String(count));
  }

  /* ---------- start ---------- */

  var started = false;
  var dataPromise = null;

  function begin(ids, apiKey) {
    if (!dataPromise) dataPromise = loadReviews(ids, apiKey);
    return dataPromise;
  }

  // Kick the request off the moment this file runs, before the list exists.
  var early = source();
  if (early && early.key) begin(early, early.key);

  function start() {
    var list = document.querySelector(LIST_SELECTOR);
    if (!list || started || document.querySelector('.magnum-review-graph')) return !!list;

    var apiKey = (early && early.key) || (window.lipscore && window.lipscore.appId);
    if (!apiKey) return false;
    started = true;

    var ids = {
      sku: (early && early.sku) || list.getAttribute('data-ls-sku') || '',
      productId: (early && early.productId) || list.getAttribute('data-ls-product-id') || '',
    };

    begin(ids, apiKey)
      .then(function (result) {
        markCount(list, (result.data && result.data.review_count) || result.reviews.length);
        if (!result.reviews.length) return;
        var preview = lipscoreHasDrawn(list) ? null : buildPreview(result.reviews);
        place(list, buildGraph(result.data, result.reviews), preview);
      })
      .catch(function () {
        /* No graph rather than a broken one - Lipscore's own list is untouched. */
      });

    return true;
  }

  /* This file runs async, often before the reviews are in the page, so it
     watches for the list to arrive rather than waiting for the page to finish. */
  if (!start()) {
    var boot = new MutationObserver(function () {
      if (start()) boot.disconnect();
    });
    boot.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('DOMContentLoaded', function () {
      if (start()) boot.disconnect();
    });
  }
})();
